import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import z from "zod";

import { usePortalUserStore } from "@/app/stores";
import {
  triggerCustomerIoShopPaymentCompleted,
  triggerCustomerIoShopPaymentFailed,
} from "@/features/analytics/triggers";
import { paymentsQueriesStore } from "@/features/payments/api";
import {
  PAYMENT_REDIRECT_STATUS,
  PAYMENT_REDIRECT_STATUS_KEY,
  SUBSCRIPTION_STATUS,
} from "@/shared/domains";

export const useHandleKlarnaRedirect = () => {
  const [searchParams] = useSearchParams();

  const user = usePortalUserStore((state) => state.portalUser);

  const redirectStatus = z
    .enum(PAYMENT_REDIRECT_STATUS)
    .safeParse(searchParams.get(PAYMENT_REDIRECT_STATUS_KEY)).data;
  const isRedirect = Boolean(redirectStatus);

  const { data: paymentToConfirm, isLoading: isLoadingRecentPayments } =
    useQuery({
      ...paymentsQueriesStore.recent(user?.code),
      enabled: isRedirect,
      select: (payments) => payments[0],
    });

  const successfulPayment =
    paymentToConfirm?.subscription.status === SUBSCRIPTION_STATUS.ACTIVE;

  useEffect(() => {
    if (!user || !paymentToConfirm || !isRedirect) {
      return;
    }

    const totalPrice = paymentToConfirm.subscription.currentProduct.price
      .discount?.isValid
      ? paymentToConfirm.subscription.currentProduct.price.discount
          .priceAfterDiscount
      : paymentToConfirm.subscription.currentProduct.price.price;

    if (
      redirectStatus === PAYMENT_REDIRECT_STATUS.FAILED ||
      !successfulPayment
    ) {
      return triggerCustomerIoShopPaymentFailed({
        user,
        productId: paymentToConfirm.subscription.currentProduct.id,
        totalPrice,
        discount: paymentToConfirm.subscription.currentProduct.price.discount,
      });
    }

    triggerCustomerIoShopPaymentCompleted({
      user,
      products: [
        {
          productCategory:
            paymentToConfirm.subscription.currentProduct.category.slug,
          productName: paymentToConfirm.subscription.currentProduct.slug,
          productId: String(paymentToConfirm.subscription.id),
          price: totalPrice,
          productFrequency:
            paymentToConfirm.subscription.currentProduct.price.durationInMonths,
        },
      ],
      totalPrice,
      discount: paymentToConfirm?.subscription.currentProduct.price.discount,
      succeededSubscriptionIds: [String(paymentToConfirm.subscription.id)],
    });
  }, [paymentToConfirm]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    subscription: paymentToConfirm?.subscription,
    isRedirect,
    isVerifyingPayment: isLoadingRecentPayments,
    isSucceededPayment: !isLoadingRecentPayments && successfulPayment,
    isFailedPayment: !isLoadingRecentPayments && !successfulPayment,
  };
};
